import config from 'config'

// eslint-disable-next-line no-control-regex
const UNSAFE_CHARACTERS = /[\u0000-\u001f\u007f\\]/

function allowedHosts(): string[] {
  const hosts = [window.location.host]
  try {
    hosts.push(new URL(config.BACKEND_ROOT_URL).host)
  } catch {
    // BACKEND_ROOT_URL is not a full URL; only this app's host is allowed
  }
  return hosts.map((host) => host.toLowerCase())
}

// Post-login redirects only go to a path on this app or to an http(s) URL on
// this app's host or the website host. Mobile auth may also allow a verification
// origin from deployment configuration, never from query parameters.
export function safeRedirectTarget(
  target: string | null | undefined,
  {
    allowRelativePaths = false,
    allowedExternalUrl
  }: {
    allowRelativePaths?: boolean
    allowedExternalUrl?: string | null
  } = {}
): string | null {
  const trimmed = target?.trim()
  if (!trimmed || UNSAFE_CHARACTERS.test(trimmed)) return null
  if (trimmed.startsWith('/')) return trimmed.startsWith('//') ? null : trimmed

  let url: URL
  try {
    url = new URL(trimmed)
  } catch {
    if (!allowRelativePaths) return null
    // Released mobile apps send paths such as `enrollment/<uuid>` without
    // a leading slash. Resolve against the app root, including on /mobile-auth/.
    try {
      url = new URL(trimmed, `${window.location.origin}/`)
    } catch {
      return null
    }
    if (url.origin !== window.location.origin) return null
    return `${url.pathname}${url.search}${url.hash}`
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null
  if (url.username || url.password) return null
  if (allowedHosts().includes(url.host.toLowerCase())) return trimmed
  if (!allowedExternalUrl || UNSAFE_CHARACTERS.test(allowedExternalUrl))
    return null
  try {
    const allowed = new URL(allowedExternalUrl)
    if (allowed.username || allowed.password) return null
    // Origin includes scheme and port, so localhost services and HTTPS
    // verification hosts cannot be swapped for another service or protocol.
    return url.origin === allowed.origin ? trimmed : null
  } catch {
    return null
  }
}
